<template>
  <div v-if="productResponse">
    <SkinnyBanner page="product" />
    <Breadcrumb
      v-if="breadcrumbResponse"
      :breads="breadcrumbResponse"
      class="bg-surface-default-low"
    />

    <RevContainer class="bg-surface-default-low pb-24 md:pb-56">
      <OutOfStock
        v-if="isOutOfStock"
        :alternative="outOfStockAlternative"
        :breadcrumb="breadcrumbResponse"
        :is-alternative-loading="outOfStockAlternativeLoading"
        :product="productResponse"
        :ratings="ratesResponse"
      />

      <div
        v-if="!isOutOfStock"
        class="flex flex-col flex-wrap md:flex-row md:flex-nowrap"
        :class="[
          {
            'items-center': isCustomizationFunnelEnabled,
          },
        ]"
      >
        <div class="relative w-full max-w-full md:w-1/3 lg:w-1/2">
          <TitleMobile
            class="block md:hidden"
            :product="productResponse"
            :rating="ratesResponse"
            :show-eco-block="ecoBlockContent.display"
          />

          <div class="top-[7rem] md:sticky md:right-1/2 md:mr-32">
            <div class="relative">
              <Gallery :images="productResponse.images" />

              <FavoritesToggle
                class="absolute right-0 top-7 md:hidden"
                :grade-id="currentGrade?.backboxGrade.value"
                is-rounded
                :offer-type="offerType"
                :price="currentGrade?.price"
                :product-uuid="productResponse.id"
                :tracking-info="productTracking.product.value"
                zone="details"
              />
            </div>
          </div>
          <AdvertisedPayment
            class="mt-20 block md:hidden"
            :current-grade="currentGrade"
          />
        </div>
        <div class="w-full max-w-full md:w-2/3 lg:w-1/2 lg:flex-1">
          <TitleDesktop
            class="hidden md:block"
            :current-grade="currentGrade"
            :product="productResponse"
            :rating="ratesResponse"
            :show-eco-block="ecoBlockContent.display"
            :swap-status="swapStatus"
            :tracking="productTracking"
          />

          <ReassuranceItems
            v-if="productResponse && currentGrade"
            :carriers="productResponse.compatibleCarriers"
            :product-category="
              productResponse.trackingDetails.category3MarketplaceCategory_ID
            "
            :shipping="currentGrade.reassurance.shipping"
            :warranty-delay="currentGrade.reassurance.defaultWarrantyDelay"
          />

          <ClientOnly v-if="!isCustomizationFunnelEnabled">
            <Pickers
              v-if="pickersResponse || !isPickersResponsePending"
              :current-grade-index="currentGradeIndex"
              :new-battery="currentGrade?.newBattery"
              :pickers="pickersResponse?.pickers ?? []"
              :tracking-details="productResponse.trackingDetails"
              @emphasis-change="onEmphasisChange"
            />
            <PickersSkeleton
              v-else
              :tracking-details="productResponse.trackingDetails"
            />

            <template #fallback>
              <PickersSkeleton
                :tracking-details="productResponse.trackingDetails"
              />
            </template>
          </ClientOnly>

          <PickersSeo
            :grade="currentGrade"
            :pickers="pickersResponse?.pickers ?? []"
          />

          <CopyLevy
            v-if="currentGrade"
            class="my-56"
            :copy-levy="currentGrade.copyLevy"
          />

          <Details
            v-if="!isCustomizationFunnelEnabled"
            :current-grade="currentGrade"
            has-swap
            :product="productResponse"
            :product-category="productCategory"
            :swap-offer="swapOffer"
            :swap-status="swapStatus"
            :technical-specifications="technicalSpecificationsResponse"
          />
        </div>
      </div>
    </RevContainer>

    <RevContainer
      v-if="isCustomizationFunnelEnabled && currentGrade"
      class="bg-surface-default-low"
    >
      <ClientOnly>
        <CustomizationFunnel
          v-if="pickersResponse || !isPickersResponsePending"
          :grade="currentGrade"
          :pickers="pickersResponse?.pickers ?? []"
          :product="productResponse"
          :product-tracking="productTracking"
          :swap-offer="swapOffer"
          :swap-status="swapStatus"
        >
          <template #sticky-bar="{ title, currentStep, totalSteps }">
            <StickyBarTop
              :current-grade="currentGrade"
              :is-customization-funnel-enabled="isCustomizationFunnelEnabled"
              :product="productResponse"
              :swap-status="swapStatus"
              :title="title"
              :tracking="productTracking"
              @hide="handleHideTopBar"
              @show="handleShowTopBar"
            >
              <template #progress-bar>
                <ProgressBar
                  :current-step="currentStep"
                  :total-steps="totalSteps"
                />
              </template>
            </StickyBarTop>

            <StickyBarTopSmall
              :is-customization-funnel-enabled="isCustomizationFunnelEnabled"
              :title="title"
              @hide="handleHideTopBar"
              @show="handleShowTopBar"
            >
              <template #progress-bar>
                <ProgressBar
                  :current-step="currentStep"
                  :total-steps="totalSteps"
                />
              </template>
            </StickyBarTopSmall>
          </template>
          <template #favorites>
            <FavoritesToggle
              class="flex justify-end"
              :grade-id="currentGrade?.backboxGrade.value"
              is-rounded
              :offer-type="offerType"
              :price="currentGrade?.price"
              :product-uuid="productResponse.id"
              :tracking-info="{
                ...productTracking.product.value,
                list: 'my_favorites_recap',
              }"
              zone="details"
            />
          </template>

          <template #details>
            <Details
              :current-grade="currentGrade"
              :has-swap="false"
              :product="productResponse"
              :product-category="productCategory"
              :should-display-vat="false"
              :show-seller-block="false"
              :swap-offer="swapOffer"
              :swap-status="swapStatus"
              :technical-specifications="technicalSpecificationsResponse"
            />
          </template>
        </CustomizationFunnel>

        <template #fallback>
          <CustomizationFunnelSkeleton />
        </template>
      </ClientOnly>
    </RevContainer>

    <RevContainer v-if="!errorReco" class="my-56" data-id="reco-section">
      <Recommendations
        :current-grade="currentGrade"
        :product="productResponse"
        @bundle-add-to-cart="onBundleAddToCart"
        @recommendations-error="onRecommendationsError"
        @recommendations-loaded="onRecommendationsLoaded"
      />
    </RevContainer>

    <RevContainer v-if="ratesResponse?.count" class="my-56">
      <ReviewsBlock
        id="reviews"
        :link="reviewsPageLink"
        :product-title="productResponse.title"
        :rating="ratesResponse"
        :rating-distribution="reviews.distribution"
        :reviews="reviews.list"
        :reviews-pending="reviews.isPending"
        :tracking-info="productTracking.product.value"
      />
    </RevContainer>

    <div class="bg-surface-default-hi">
      <RevContainer>
        <PopularSearches
          v-if="tagsResponse"
          class="mb-32 md:mb-72"
          :tags="tagsResponse"
        />
      </RevContainer>
      <EcoBlock
        :content="ecoBlockContent"
        data-id="eco-block"
        :product-tracking="productTracking"
      />
      <RevContainer class="pt-32 md:pt-72">
        <SeoContent
          v-if="technicalSpecificationsResponse"
          class="mb-72"
          data-id="seo-technical-specs"
          :description="productResponse.description ?? ''"
          :technical-specifications="technicalSpecificationsResponse"
          :title="productResponse.title"
        />

        <AboutUs />
        <TrustList class="my-32 md:my-72" data-qa="product-reassurance" />
        <TrustCarousel class="my-32 md:my-56" />
      </RevContainer>
    </div>

    <ClientOnly>
      <SwapModal
        v-if="swapStatus !== SWAP_DISABLED"
        :datalayer-category="productCategory"
        :modal-name="BUYBACK_MODAL_NAMES.SWAP_MIX_AND_MATCH"
        :price="swapOffer?.price || ''"
        :product-title="productResponse.rawTitle"
        :redirect-on-close-target="CHECKOUT.CART"
        :swap-listings="swapListings"
      />

      <SwapModal
        v-if="swapStatus !== SWAP_DISABLED"
        :datalayer-category="productCategory"
        :modal-name="BUYBACK_MODAL_NAMES.SWAP_ATC"
        :price="swapOffer?.price || ''"
        :product-title="productResponse.rawTitle"
        :redirect-on-close-target="CHECKOUT.CART"
        :swap-listings="swapListings"
        @continue="handleContinue"
      />

      <AddToCartModal
        v-if="currentGrade"
        :additional-listings="additionalListings"
        :listing-id="currentGrade.id"
        :title="productResponse.rawTitle"
      />
      <StickyBarTop
        v-if="
          !isOutOfStock &&
          productResponse &&
          currentGrade &&
          !isCustomizationFunnelEnabled
        "
        :current-grade="currentGrade"
        :emphasis-color="emphasisColor"
        :emphasis-label="emphasisLabel"
        :product="productResponse"
        :title="productResponse.rawTitle"
        :tracking="productTracking"
      />
      <StickyBarBottom
        v-if="!isOutOfStock && productResponse && currentGrade"
        :emphasis-color="emphasisColor"
        :emphasis-label="emphasisLabel"
        :offer="currentGrade"
        :product="productResponse"
        :tracking="productTracking"
      />
    </ClientOnly>
  </div>
</template>

<script lang="ts" setup>
import { useRoute, useRuntimeConfig } from '#imports'
import { computed, ref } from 'vue'

import { productAPI } from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { RevContainer } from '@ds/components/Container'
import { openModal } from '@ds/components/ModalBase'

import { useRouteLocationWithLocale } from '~/composables/useRouteLocationWithLocale'
import SkinnyBanner from '~/scopes/branding/components/SkinnyBanner/SkinnyBanner.vue'
import { MODAL_NAMES as BUYBACK_MODAL_NAMES } from '~/scopes/buyback/constants'
import { SWAP_DISABLED } from '~/scopes/buyback/swap/components/SwapBlock/constants'
import SwapModal from '~/scopes/buyback/swap/components/SwapModal/SwapModal.vue'
import { useSwapEstimations } from '~/scopes/buyback/swap/composables/useSwapEstimations'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Product } from '~/scopes/core/models/product'
import FavoritesToggle from '~/scopes/dashboard/favorites/toggle/FavoritesToggle.vue'
import TrustList from '~/scopes/reassurance/components/TrustList/TrustList.vue'
import ReviewsBlock from '~/scopes/reviews/components/ReviewsBlock/ReviewsBlock.vue'
import { ROUTES as REVIEWS_ROUTES } from '~/scopes/reviews/route-names'

import AboutUs from '../components/AboutUs/AboutUs.vue'
import AddToCartModal from '../components/AddToCartModal/AddToCartModal.vue'
import AdvertisedPayment from '../components/AdvertisedPayment/AdvertisedPayment.vue'
import Breadcrumb from '../components/Breadcrumb/Breadcrumb.vue'
import CopyLevy from '../components/CopyLevy/CopyLevy.vue'
import CustomizationFunnel from '../components/CustomizationFunnel/CustomizationFunnel.vue'
import ProgressBar from '../components/CustomizationFunnel/components/ProgressBar/ProgressBar.vue'
import CustomizationFunnelSkeleton from '../components/CustomizationFunnel/components/Skeleton/Skeleton.vue'
import StickyBarTopSmall from '../components/CustomizationFunnel/components/StickyBarTopSmall/StickyBarTopSmall.vue'
import Details from '../components/Details/Details.vue'
import EcoBlock from '../components/EcoBlock/EcoBlock.vue'
import Gallery from '../components/Gallery/Gallery.vue'
import OutOfStock from '../components/OutOfStock/OutOfStock.vue'
import Pickers from '../components/Pickers/Pickers.vue'
import PickersSeo from '../components/Pickers/components/Seo/Seo.vue'
import PickersSkeleton from '../components/Pickers/components/Skeleton/Skeleton.vue'
import PopularSearches from '../components/PopularSearches/PopularSearches.vue'
import ReassuranceItems from '../components/ReassuranceItems/ReassuranceItems.vue'
import Recommendations from '../components/Recommendations/Recommendations.vue'
import SeoContent from '../components/SeoContent/SeoContent.vue'
import StickyBarBottom from '../components/StickyBarBottom/StickyBarBottom.vue'
import StickyBarTop from '../components/StickyBarTop/StickyBarTop.vue'
import TitleDesktop from '../components/Title/TitleDesktop.vue'
import TitleMobile from '../components/Title/TitleMobile.vue'
import TrustCarousel from '../components/TrustCarousel/TrustCarousel.vue'
import { useAddGradeInUrl } from '../composables/useAddGradeInUrl'
import { useHandleIncorrectRouteParams } from '../composables/useHandleIncorrectRouteParams'
import { useProductHead } from '../composables/useProductHead'
import { useProductRematch } from '../composables/useProductRematch'
import { useProductRequests } from '../composables/useProductRequests'
import { useProductTracking } from '../composables/useProductTracking'
import { useUrlParams } from '../composables/useUrlParams'
import { MODAL_NAMES } from '../constants'

const route = useRoute()
const marketPlace = useMarketplace()
const experiments = useExperiments()

// This has to be called before making the requests as we wanna reduce 404 errors
useHandleIncorrectRouteParams()

const { grade: urlGrade, offerType, productId } = useUrlParams()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const additionalListings = ref<Array<string>>([])
const outOfStockAlternativeLoading = ref(true)
const outOfStockAlternative = ref<Product | null>(null)
const emphasisColor = ref<'info' | 'accent' | undefined>(undefined)
const emphasisLabel = ref<string | undefined>(undefined)
const errorReco = ref(false)

const {
  productResponse,
  bestOffersResponse,
  breadcrumbResponse,
  technicalSpecificationsResponse,
  tagsResponse,
  ecoBlockResponse,
  pickersResponse,
  isPickersResponsePending,
  ratesResponse,
  reviewsResponse,
  isReviewsResponsePending,
  ratingDistributionResponse,
  isOutOfStock,
  currentGrade,
} = await useProductRequests(productId, urlGrade, offerType)

const { swapStatus, swapListings, swapOffer } = useSwapEstimations({
  isSwapEligible: productResponse.value?.isSwapEligible || false,
  grade: currentGrade,
  bestOffersResponse,
})

const currentGradeIndex = computed(() => {
  if (!bestOffersResponse.value) {
    return null
  }

  return bestOffersResponse.value.findIndex(
    ({ backboxGrade }) => urlGrade.value === backboxGrade.value,
  )
})

const reviews = computed(() => ({
  isPending: isReviewsResponsePending.value,
  list: reviewsResponse.value ? reviewsResponse.value.results : [],
  distribution: ratingDistributionResponse.value ?? [],
}))

const reviewsPageLink = computed(() => {
  const { FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_REVIEWS = '' } =
    useRuntimeConfig().public

  if (
    FF_ENABLED_COUNTRIES_FRONT_APPS_ROUTE_REVIEWS.includes(
      marketPlace.market.countryCode,
    )
  ) {
    return resolveLocalizedRoute({
      name: REVIEWS_ROUTES.PAGES.PRODUCT,
      params: route.params,
    })
  }

  return resolveLocalizedRoute({
    name: REVIEWS_ROUTES.PAGES.LEGACY_PRODUCT,
    params: { uuid: route.params.uuid },
    query: { productName: productResponse.value?.title },
  })
})

const productCategory = computed(() => {
  return (
    (productResponse.value !== null &&
      productResponse.value.trackingDetails &&
      productResponse.value.trackingDetails.category3Name) ||
    ''
  )
})

const ecoBlockContent = computed(() => {
  return ecoBlockResponse.value ?? productAPI.getEcoBlockDefaultValue
})

const isCustomizationFunnelEnabled = computed(() => {
  return (
    !isOutOfStock.value &&
    productResponse.value?.trackingDetails.category3MarketplaceCategory_ID ===
      2 &&
    experiments['experiment.ppMixAndMatch'] === 'withMixAndMatch'
  )
})

useProductRematch(productResponse)
useAddGradeInUrl(currentGrade)
useProductHead(productResponse, currentGrade, ratesResponse, isOutOfStock)
const productTracking = useProductTracking(
  productResponse,
  currentGrade,
  bestOffersResponse,
  ratesResponse,
)

function onBundleAddToCart(listings: Array<string>) {
  additionalListings.value = listings
}

function onRecommendationsLoaded(products: Array<Product>) {
  outOfStockAlternative.value = products[0]
  outOfStockAlternativeLoading.value = false
}

function onRecommendationsError() {
  errorReco.value = true
  outOfStockAlternativeLoading.value = false
}

function onEmphasisChange(
  newEmphasis: 'info' | 'accent' | undefined,
  label: string | undefined,
) {
  emphasisColor.value = newEmphasis
  emphasisLabel.value = label
}

function handleContinue() {
  setTimeout(() => {
    openModal(MODAL_NAMES.ADD_TO_CART)
  })
}
function handleShowTopBar() {
  const header = document.getElementById('header')

  if (header && header !== null) {
    header.style.visibility = 'hidden'
  }
}
function handleHideTopBar() {
  const header = document.getElementById('header')

  if (header && header !== null) {
    header.style.visibility = 'visible'
  }
}
</script>

<style>
/* On mobile, we need to add some padding to be able to see everything in the footer due to the sticky add to cart bar */
@media only screen and (max-width: 767px) {
  #main-footer {
    padding-bottom: 100px !important;
  }
}
</style>
